import React, {
  useLayoutEffect,
  useReducer,
  useContext,
  useCallback,
  createContext
} from "react";
import PropTypes from "prop-types";
import { UnifiedHeader } from "valuations-journey";
import reducer from "./reducer";
import { useSelector } from "react-redux";
import { getSourceSystemContent, selectAppraisal } from "../../store/selectors";
import { useSourceSystem } from "../../hooks/useSourceSystem";

const PageContext = createContext();

function usePageContext() {
  const context = useContext(PageContext);
  if (context === undefined) {
    throw new Error(
      "PageLayout components can not be rendered outside of the PageLayout component"
    );
  }
  return context;
}

const PageLayout = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    stickyHeight: 0,
    isSticky: false,
    bottomFixedHeight: 0
  });

  const setScroll = () => dispatch({ type: "SET_SCROLL" });
  const fixBottomHeight = useCallback(
    (data) => dispatch({ type: "SET_BOTTOM_FIXED_HEIGHT", data }),
    []
  );

  const contentCss = {
    paddingTop: `${state.stickyHeight}px`,
    paddingBottom: `${state.bottomFixedHeight}px`
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);

    if (document.body.scrollHeight > document.body.clientHeight) {
      dispatch({ type: "SET_SCROLLABLE", data: true });
    } else {
      dispatch({ type: "SET_SCROLLABLE", data: false });
    }
  }, []);

  return (
    <PageContext.Provider
      value={{
        contentCss,
        setScroll,
        fixBottomHeight,
        ...state
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

const Header = ({ step, title, percentageValue } = null) => {
  const { stepCount, headerTitle } = useSelector(getSourceSystemContent);
  const {
    sourceSystem: { exitCopyValue, exitUri, percentageStepper, haveHeaderExit }
  } = useSourceSystem();
  const startOffSet = percentageStepper?.startOffSet;
  const endOffSet = percentageStepper?.endOffSet;
  return (
    <div className="ch-bg--white">
      <UnifiedHeader
        journeyTitle={headerTitle}
        pageName={title}
        handleExit={() => window.location.assign(exitUri)}
        currentstep={step}
        steps={stepCount}
        startOffSet={startOffSet}
        endOffSet={endOffSet}
      />
    </div>
  );
};

const Content = ({
  children,
  backgroundColour,
  showPromoBranding = false,
  removeBottomPadding = false
}) => {
  const { contentCss } = usePageContext();
  return (
    <div
      style={contentCss}
      className={`${removeBottomPadding ? "content-container--no-padding" : "content-container"} ${backgroundColour || ""} ${showPromoBranding ? "promo-branding" : ""}`}
    >
      <div
        className={`ch-container ${showPromoBranding ? "" : "ch-mt--4 lg:ch-mt--7"} sm:ch-width--6 md:ch-width--5 ch-width--12 sl:ch-width--8 lg:ch-width--4 page-layout--content`}
      >
        {children}
      </div>
    </div>
  );
};

const CompactView = ({ children }) => {
  const { contentCss } = usePageContext();

  return (
    <div style={contentCss} className="content-container">
      <div className="page-layout--content-compact">{children}</div>
    </div>
  );
};

const Footer = ({
  children,
  backgroundColour,
  position,
  showShadow = true
}) => {
  const { fixBottomHeight } = usePageContext();
  const measureOffsetHeight = useCallback(
    (node) => {
      if (children && node !== null) {
        fixBottomHeight(node.offsetHeight);
      }
    },
    [children, fixBottomHeight]
  );

  const shadowEffect =
    document.documentElement.scrollHeight >
      document.documentElement.clientHeight &&
    !(window.innerHeight + window.scrollY >= document.body.offsetHeight);

  return (
    <div
      ref={measureOffsetHeight}
      className={`${position ? position : "page-layout--bottom-fixed"} ${
        backgroundColour ? backgroundColour : "ch-bg--grey-1"
      } ${shadowEffect && showShadow ? "scrollable " : ""}`}
    >
      {children}
    </div>
  );
};

Header.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  step: PropTypes.number
};

Content.propTypes = {
  children: PropTypes.node.isRequired
};

CompactView.propTypes = {
  children: PropTypes.node.isRequired
};

PageLayout.propTypes = {
  children: PropTypes.node.isRequired
};

PageLayout.Header = Header;
PageLayout.Content = Content;
PageLayout.CompactView = CompactView;
PageLayout.Footer = Footer;

export default PageLayout;
